import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import type { SpotifyCanvasSongResponse } from 'fym-shared/src/music24/spotifyCanvas';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../common/types';
import { catalogArtworkSrc } from '../../../smartLinks/catalog/catalogArtwork';
import { ServiceExtras } from '../extras/ServiceExtras';
import { ServiceExtraAction, ServiceExtraAutoUpload, ServiceExtraItem } from '../extras/serviceExtraTypes';
import { spotifyCanvasRowView, spotifyTrackUrl } from './spotifyCanvasRowRules';
import { spotifyCanvasMessages as m, spotifyCanvasSwitchMessages as sm } from './spotifyCanvasMessages';
import {
  CANVAS_ACTION_LABELS,
  canvasChipText,
  canvasChipTone,
  canvasDay,
  canvasFileText,
  canvasSentenceText,
} from './spotifyCanvasTexts';
import { CanvasLoopThumb } from './CanvasLoopThumb';
import { SpotifyCanvasTermsDialog } from './SpotifyCanvasTermsDialog';
import { useSpotifyCanvasRowActions } from './useSpotifyCanvasRowActions';
import { useSpotifyCanvasSwitch } from './useSpotifyCanvasSwitch';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for ServicesPanel; each part uses its own slice of it */
import panelStyles from '../ServicesPanel.module.scss';
import styles from './SpotifyCanvas.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SpotifyExtrasProps {
  accountId: number;
  item: CatalogItem;
  data: SpotifyCanvasSongResponse;
  /** The demo artist: no button, the switch dimmed (design spotify-canvas d3 note). */
  readOnly: boolean;
  refetch: () => void;
  hide: () => void;
}

/**
 * "Spotify extras" in the Spotify row of "Where it plays" (design spotify-canvas d3, d4, option B):
 * the Canvas line (the 9:16 loop, the chips, the state sentence, its button) and the Auto upload
 * switch in place. It gives one item to the generic `ServiceExtras` box; later Spotify extras add
 * items here. The states and the copy are those of frames a2, a3, b2.
 */
const SpotifyExtrasComponent: React.FC<SpotifyExtrasProps> = ({ accountId, item, data, readOnly, refetch, hide }) => {
  const intl = useIntl();
  const view = useMemo(() => spotifyCanvasRowView(data, readOnly), [data, readOnly]);
  const actions = useSpotifyCanvasRowActions({ accountId, data, refetch, hide });
  const toggle = useSpotifyCanvasSwitch({ accountId, settings: data.settings, readOnly });
  const { upload, signIn, run, isBusy } = actions;
  const artwork = catalogArtworkSrc(item, 96);

  let actionError: string | null = null;
  if (upload.error?.kind === 'file') {
    actionError = canvasFileText(intl, upload.error.rule);
  } else if (upload.error?.kind === 'generic' || signIn.error !== undefined) {
    actionError = intl.formatMessage(m.didNotWork);
  }

  const canvasItem = useMemo((): ServiceExtraItem => {
    const itemActions = view.actions.flatMap(({ action, look }): ServiceExtraAction[] => {
      const label = intl.formatMessage(CANVAS_ACTION_LABELS[action]);
      if (action === 'open') {
        return data.spotifyTrackId === null
          ? []
          : [{ id: action, label, look: 'link', href: spotifyTrackUrl(data.spotifyTrackId) }];
      }
      return [{ id: action, label, look, disabled: isBusy, onClick: () => run(action) }];
    });
    const termsAt = data.settings.termsAcceptedAt;
    let sub = intl.formatMessage(sm.off);
    if (readOnly) {
      sub = intl.formatMessage(sm.demo);
    } else if (toggle.isOn) {
      sub = intl.formatMessage(sm.onFor, { artist: item.artistName });
    }
    const autoUpload: ServiceExtraAutoUpload = {
      title: intl.formatMessage(m.autoUploadTitle),
      sub,
      switchLabel: intl.formatMessage(sm.label),
      isOn: !readOnly && toggle.isOn,
      isSaving: toggle.isSaving,
      isDimmed: readOnly,
      onToggle: toggle.toggle,
      note:
        !readOnly && toggle.isOn && termsAt !== null
          ? intl.formatMessage(sm.termsAcceptedCanvas, { date: canvasDay(intl, termsAt) })
          : null,
      error: toggle.failed ? intl.formatMessage(m.didNotWork) : null,
    };
    const details = (
      <>
        {view.watermark && (
          <span className={styles.watermark}>
            <span className={clsx(panelStyles.chip, panelStyles.chipMuted)}>{intl.formatMessage(m.chipWatermark)}</span>
            {withBrandWord(intl.formatMessage(m.watermark))}
          </span>
        )}
        {view.newer && (
          <span className={styles.newer}>
            <FontAwesomeIcon icon={faArrowsRotate} />
            {intl.formatMessage(m.newer)}
          </span>
        )}
        {view.progress && (
          <div className={styles.progress} role="progressbar" aria-label={intl.formatMessage(m.title)} />
        )}
      </>
    );
    return {
      id: 'spotifyCanvas',
      title: intl.formatMessage(m.title),
      thumb: <CanvasLoopThumb thumb={view.thumb} dot={view.dot} artwork={artwork} />,
      chips: view.chips.map((chip) => ({ id: chip, text: canvasChipText(intl, chip), tone: canvasChipTone(chip) })),
      status: canvasSentenceText(intl, view.sentence),
      statusIsBad: view.isBad,
      details,
      actions: itemActions,
      actionError,
      autoUpload,
      waits: view.waits,
    };
  }, [
    actionError,
    artwork,
    data.settings.termsAcceptedAt,
    data.spotifyTrackId,
    intl,
    isBusy,
    item.artistName,
    readOnly,
    run,
    toggle.failed,
    toggle.isOn,
    toggle.isSaving,
    toggle.toggle,
    view,
  ]);
  const items = useMemo(() => [canvasItem], [canvasItem]);

  return (
    <>
      <ServiceExtras label={intl.formatMessage(m.extrasLabel)} items={items} testId="spotify-extras" />
      <SpotifyCanvasTermsDialog
        isOpen={upload.termsOpen}
        mode="upload"
        artistName={item.artistName}
        songTitle={item.title}
        isBusy={upload.isUploading}
        failed={upload.termsFailed}
        onAccept={upload.acceptAndUpload}
        onCancel={upload.closeTerms}
      />
      <SpotifyCanvasTermsDialog
        isOpen={toggle.termsOpen}
        mode="autoUpload"
        artistName={item.artistName}
        isBusy={toggle.isSaving}
        failed={toggle.termsFailed}
        onAccept={toggle.acceptAndTurnOn}
        onCancel={toggle.closeTerms}
      />
    </>
  );
};

export const SpotifyExtras = TypedMemo(SpotifyExtrasComponent);
